import { create } from 'zustand'
import type { ActiveView } from '@/types'

interface UIState {
  // Navigation
  activeView: ActiveView
  activeProjectId: string | null
  activeFolderId: string | null
  activeThreadId: string | null

  // UI panels
  sidebarOpen: boolean
  rightPanelOpen: boolean
  rightPanelTab: 'metadata' | 'files' | 'decisions' | 'activity'

  // Model selection (for new messages)
  selectedProvider: string
  selectedModel: string

  // Actions
  navigateToDashboard: () => void
  navigateToProject: (projectId: string) => void
  navigateToThread: (threadId: string, projectId: string) => void
  navigateToSettings: () => void
  navigateToWallet: () => void
  navigateToMarketplace: () => void
  navigateToAdmin: () => void
  navigateToApiKeys: () => void
  navigateToAgent: () => void
  navigateToSocialAgent: () => void
  navigateToContentSeo: () => void
  navigateToAppBuilder: (appProjectId?: string) => void
  navigateToFiles: () => void
  activeAppProjectId: string | null
  setActiveFolderId: (folderId: string | null) => void
  setSidebarOpen: (open: boolean) => void
  toggleSidebar: () => void
  setRightPanelOpen: (open: boolean) => void
  toggleRightPanel: () => void
  setRightPanelTab: (tab: UIState['rightPanelTab']) => void
  setSelectedProvider: (provider: string) => void
  setSelectedModel: (model: string) => void
}

export const useUIStore = create<UIState>((set) => ({
  activeView: 'dashboard',
  activeProjectId: null,
  activeFolderId: null,
  activeThreadId: null,
  activeAppProjectId: null,
  sidebarOpen: true,
  rightPanelOpen: true,
  rightPanelTab: 'metadata',
  selectedProvider: '',
  selectedModel: '',

  navigateToDashboard: () => set({
    activeView: 'dashboard',
    activeProjectId: null,
    activeFolderId: null,
    activeThreadId: null,
  }),

  navigateToProject: (projectId: string) => set({
    activeView: 'project',
    activeProjectId: projectId,
    activeFolderId: null,
    activeThreadId: null,
  }),

  navigateToThread: (threadId: string, projectId: string) => set({
    activeView: 'thread',
    activeProjectId: projectId,
    activeThreadId: threadId,
  }),

  navigateToSettings: () => set({
    activeView: 'settings',
    activeThreadId: null,
  }),

  navigateToWallet: () => set({
    activeView: 'wallet',
    activeThreadId: null,
  }),

  navigateToMarketplace: () => set({
    activeView: 'marketplace',
    activeThreadId: null,
  }),

  navigateToAdmin: () => set({
    activeView: 'admin',
    activeThreadId: null,
  }),

  navigateToApiKeys: () => set({
    activeView: 'api-keys',
    activeThreadId: null,
  }),

  navigateToAgent: () => set({
    activeView: 'agent',
    activeThreadId: null,
  }),

  navigateToSocialAgent: () => set({
    activeView: 'social-agent',
    activeThreadId: null,
  }),

  navigateToContentSeo: () => set({
    activeView: 'content-seo',
    activeThreadId: null,
  }),

  navigateToAppBuilder: (appProjectId?: string) => set({
    activeView: 'app-builder',
    activeThreadId: null,
    activeAppProjectId: appProjectId || null,
  }),

  navigateToFiles: () => set({
    activeView: 'files',
    activeThreadId: null,
  }),

  setActiveFolderId: (folderId: string | null) => set({ activeFolderId: folderId }),
  setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),
  toggleSidebar: () => set((s: UIState) => ({ sidebarOpen: !s.sidebarOpen })),
  setRightPanelOpen: (open: boolean) => set({ rightPanelOpen: open }),
  toggleRightPanel: () => set((s: UIState) => ({ rightPanelOpen: !s.rightPanelOpen })),
  setRightPanelTab: (tab: UIState['rightPanelTab']) => set({ rightPanelTab: tab }),
  setSelectedProvider: (provider: string) => set({ selectedProvider: provider }),
  setSelectedModel: (model: string) => set({ selectedModel: model }),
}))
